
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import ManageSidemenuLayout from '@/components/organisms/layout/ManageSidemenuLayout.vue'
import RegistDataPanel from '@/components/organisms/manage/RegistDataPanel.vue'
import TitlePanel from '@/components/organisms/TitlePanel.vue'
import StudentListPanel from '@/components/organisms/manage/StudentListPanel.vue'
import MenuEditStudent from '@/components/organisms/floatmenu/MenuEditStudent.vue'
import MenuEditTemporaryStudent from '@/components/organisms/floatmenu/MenuEditTemporaryStudent.vue'
import { UserStatus } from '@/const/manage/UserStatus'
import { StudentList } from '@/models/manage/StudentList'
import { SelectItem } from '@/models/SelectItem'

/**
 * 生徒管理(塾)
 */
@Component({
  components: {
    ManageSidemenuLayout,
    RegistDataPanel,
    TitlePanel,
    StudentListPanel,
    MenuEditStudent,
    MenuEditTemporaryStudent,
  },
})
export default class StudentListLayout extends Vue {
  /** サイドメニュー部 */
  @Ref()
  private manageSidemenuLayout!: ManageSidemenuLayout

  /** 生徒一覧パネル */
  @Ref()
  studentListPanel!: StudentListPanel

  /** 編集メニュー */
  @Ref()
  private menuEditStudent!: MenuEditStudent

  /** 仮ユーザ編集メニュー */
  @Ref()
  private menuEditTemporaryStudent!: MenuEditTemporaryStudent

  /** 先生権限画面の場合true */
  @Prop({ default: false })
  private isTeacher!: boolean

  /** 塾管理者権限画面の場合true */
  @Prop({ default: false })
  private isAcademy!: boolean

  /** アクティベート済か否か */
  @Prop()
  private isActivated!: boolean

  /** 生徒情報一覧 */
  @Prop()
  private studentList!: StudentList[]

  /** ユーザのアクティベート状態フィルターの選択肢 */
  @Prop()
  private filterUserActivateStatusList!: SelectItem[]

  /** ユーザのアクティベート状態フィルターの初期選択値 */
  @Prop()
  private defaultUserActivateStatus!: string

  /** 学年フィルターの選択肢 */
  @Prop()
  private filterGradeList!: SelectItem[]

  /** 学年フィルターの初期選択値 */
  @Prop()
  private defaultGardeCode!: string

  /** 教室フィルターの選択肢 */
  @Prop()
  private filterBranchList?: SelectItem[]

  /** コースフィルターの選択肢 */
  @Prop()
  private filterAcademyCourseList!: SelectItem[]

  /** クラスフィルターの選択肢 */
  @Prop()
  private filterAcademyClassList!: SelectItem[]

  /** クラスフィルターのdisabled制御 */
  @Prop()
  private isClassFilterDisabled!: boolean

  /** ページ数 */
  @Prop()
  private pageLength!: number

  /** 現在のページ */
  @Prop()
  private currentPage!: number

  /** 生徒登録ボタン押下時のイベント */
  @Prop()
  private eventClickCreateStudentButton?: () => Promise<void>

  /** ユーザのアクティベート状態フィルター変更時のイベント */
  @Prop()
  private changeUserActivateStatusFilter!: () => Promise<void>

  /** 学年フィルター変更時のイベント */
  @Prop()
  private changeGradeFilter!: () => Promise<void>

  /** 教室フィルター変更時のイベント */
  @Prop()
  private changeBranchFilter?: () => Promise<void>

  /** コースフィルター変更時のイベント */
  @Prop()
  private changeCourseFilter!: () => Promise<void>

  /** クラスフィルター変更時のイベント */
  @Prop()
  private changeClassFilter!: () => Promise<void>

  /** 生徒検索ボタン押下時のイベント */
  @Prop()
  private eventSearchStudent!: () => Promise<void>

  /** アップロードボタン押下時のイベント */
  @Prop()
  private eventClickUpload?: () => void

  /** ダウンロードボタン押下時のイベント */
  @Prop()
  private eventClickDownload!: () => Promise<void>

  /** 削除ボタン押下時のイベント */
  @Prop()
  private eventClickDeleteButton?: (userId: number) => Promise<void>

  /** ユーザデータ編集ボタン押下時のイベント */
  @Prop()
  private eventClickEditUserDataButton?: (userId: number) => Promise<void>

  /** パスワード編集ボタン押下時のイベント */
  @Prop()
  private eventClickEditPasswordButton!: (userId: number) => void

  /** 生徒有効化ボタン押下時のイベント */
  @Prop()
  private eventClickUnlockButton?: (userId: number) => Promise<void>

  /** 生徒休止ボタン押下時のイベント */
  @Prop()
  private eventClickLockButton?: (userId: number) => Promise<void>

  /** ページリンクボタン押下時のイベント */
  @Prop()
  private changePage!: (value: number) => Promise<void>

  /**
   * 編集ボタンクリック時の処理
   *
   * @param event イベント
   * @param userId 対象ユーザID
   * @param lockedStatus 休止状態
   * @param registerType 登録種別
   */
  private async eventClickEditButton(
    event: Event,
    userId: number,
    lockedStatus: string,
    registerType: number | null
  ): Promise<void> {
    if (this.isActivated) {
      // ユーザのロック状態を判定
      const isLocked = UserStatus.LOCKED === lockedStatus
      await this.menuEditStudent.show(
        event.target as HTMLElement,
        userId,
        isLocked,
        registerType,
        this.manageSidemenuLayout.layoutByRole.sidemenuLayout.contentMain
      )
    } else {
      await this.menuEditTemporaryStudent.show(
        event.target as HTMLElement,
        userId,
        registerType,
        this.manageSidemenuLayout.layoutByRole.sidemenuLayout.contentMain
      )
    }
  }

  /**
   * 選択中の学年
   *
   * @returns 学年コード
   */
  public currentGrade(): string {
    return this.studentListPanel.currentGrade()
  }

  /**
   * 選択中の教室
   *
   * @returns 教室コード
   */
  public currentBranch(): string {
    return this.studentListPanel.currentBranch()
  }

  /**
   * 教室選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentBranchValue(value: string): void {
    this.studentListPanel.setCurrentBranchValue(value)
  }

  /**
   * 選択中のコース
   *
   * @returns コースコード
   */
  public currentCourse(): string {
    return this.studentListPanel.currentCourse()
  }

  /**
   * コース選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentCourseValue(value: string): void {
    this.studentListPanel.setCurrentCourseValue(value)
  }

  /**
   * 選択中のクラス
   *
   * @returns クラスコード
   */
  public currentClass(): string {
    return this.studentListPanel.currentClass()
  }

  /**
   * クラス選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentClassValue(value: string): void {
    this.studentListPanel.setCurrentClassValue(value)
  }

  /**
   * 検索欄に入力された文字列
   *
   * @returns 入力された文字列
   */
  public textInputedSearch(): string {
    return this.studentListPanel.textInputed()
  }

  /**
   * チェック済みの生徒ID一覧
   *
   * @returns 生徒ID一覧
   */
  public checkedStudentList(): number[] {
    return this.studentListPanel.checkedStudentList()
  }

  /**
   * 選択済生徒リストのクリア
   */
  public clearSelectedStudents(): void {
    this.studentListPanel.clearSelectedStudents()
  }
}
